import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Close from '@mui/icons-material/Close';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import {
	AddFaq,
	InformationQaOverviewDTO,
} from '@/api/DTO/information/faq.interface';
import { FormType } from '@/api/enum/formType.enum';
import { useCreateMomentMutation } from '@/api/app-setting/momentApiSlice';

interface Props {
	type: FormType;
	row?: InformationQaOverviewDTO;
}

export default function AddPageDialog(props: Props) {
	const { type, row } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [createMoment] = useCreateMomentMutation();

	const defaultValues: DefaultValues<AddFaq> = row ? row : {};

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
	});

	useEffect(() => {
		if (row) {
			methods.reset(row);
		}
	}, [row]);

	const methods = useForm<AddFaq>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset();
		setOpen(false);
	};

	const onSubmit: SubmitHandler<AddFaq> = async (data) => {
		const response = await createMoment(data);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.createPageSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<>
			<Button
				endIcon={<DriveFileRenameOutlineOutlinedIcon />}
				onClick={() => {
					setOpen(true);
				}}
				sx={{ marginRight: '24px' }}
				variant='contained'
			>
				{t('faq.addPage')}
			</Button>

			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{type == FormType.create ? t('faq.addPage') : t('faq.changePage')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='titleZh'
										label={t('moment.pageTitleZh')}
										maxLength={20}
									/>
								</Grid>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='titleEn'
										label={t('moment.pageTitleEn_')}
										maxLength={100}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.isConfirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
